import type { Location } from "react-router-dom";
import { getDefaultSdPath, type SdAppRole } from "@sd/lib/sdPaths";
import { isAgentShellRole } from "@sd/lib/sdRoles";
import { TICKET_DETAIL_RETURN_TO } from "@sd/lib/ticketDetailPath";
import {
  isEmailTicketUuidSegment,
  parseTicketIdFromEmailSegment,
} from "@sd/lib/ticketEmailDeepLink";

const SD_RESERVED_TOP_SEGMENTS = new Set([
  "admin",
  "tickets",
  "ticket",
  "login",
  "my-portal",
  "customer",
  "assets",
  "knowledge-base",
  "reports",
  "users",
  "supervisor",
  "widget-embed",
]);

/** Email notification link — not in-app admin/agent navigation with `returnTo`. */
export function isSdEmailTicketDeepLink(
  from: Location | null | undefined
): boolean {
  const pathname = from?.pathname?.trim() ?? "";
  if (!pathname.startsWith("/sd/") || pathname === "/sd/login") {
    return false;
  }

  const normalized = pathname.replace(/\/+$/, "") || pathname;

  const shortTicket = normalized.match(/^\/sd\/ticket\/([^/]+)$/)?.[1];
  if (parseTicketIdFromEmailSegment(shortTicket)) {
    return true;
  }

  const topSegment = normalized.match(/^\/sd\/([^/]+)$/)?.[1];
  if (
    topSegment &&
    !SD_RESERVED_TOP_SEGMENTS.has(topSegment) &&
    isEmailTicketUuidSegment(topSegment)
  ) {
    return true;
  }

  const viewId = normalized.match(/^\/sd\/tickets\/view\/([^/]+)$/)?.[1];
  if (!viewId) {
    return false;
  }

  const returnTo =
    new URLSearchParams(from?.search ?? "").get(TICKET_DETAIL_RETURN_TO)?.trim() ??
    "";
  // In-app navigation (admin/agent eye) always sets `returnTo`; bare view URL = email.
  return !returnTo;
}

/** Post-login redirect: email deep links restore; role defaults for admin/agent/super agent. */
export function resolveSdPostLoginPath(
  role: SdAppRole | null | undefined,
  from: Location | null | undefined
): string {
  if (!from?.pathname?.startsWith("/sd/") || from.pathname.trim() === "/sd/login") {
    return getDefaultSdPath(role ?? null);
  }

  if (isSdEmailTicketDeepLink(from)) {
    return `${from.pathname}${from.search ?? ""}`;
  }

  if (
    role === "ServiceDesk_Admin" ||
    isAgentShellRole(role) ||
    role === "ServiceDesk_SuperAgent"
  ) {
    return getDefaultSdPath(role);
  }

  return `${from.pathname}${from.search ?? ""}`;
}

/** @deprecated Use {@link resolveSdPostLoginPath} with role after login. */
export function resolveSdReturnPath(from: Location | null | undefined): string {
  return resolveSdPostLoginPath(null, from);
}
